import { DatatableCell, DatatableRow } from '@monksflow/monks-ui';
import { useNavigate } from 'react-router';

import { ROUTES } from '@/shared/config/routes';
import { TABLE_COL_WIDTH } from '@/shared/constants';
import { formatRelativeDate } from '@/shared/utils/dateUtils';

import { collectAllTools } from '../AgentTree';
import { ToolNameCell } from './ToolNameCell';
import { ToolRowActions } from './ToolRowActions';
import { ToolTypeCell } from './ToolTypeCell';
import { WorkspaceAccessCell } from './WorkspaceAccessCell';
import type { ToolsTableV2RowProps } from './ToolsTableV2.types';

const EM_DASH = '—';

/**
 * Where the row leads: the same place its menu does. A built agent opens in the builder, which
 * only a custom one offers; anything else opens its config page unless it has none.
 */
function getDetailRoute({
  tool,
  canConfigure,
  isManaged,
  canEditAgent,
}: {
  tool: ToolsTableV2RowProps['tool'];
  canConfigure: boolean;
  isManaged: boolean;
  canEditAgent: boolean;
}): string | undefined {
  if (tool.builtAgent !== undefined) {
    return tool.source === 'user' && canEditAgent ? ROUTES.ADMIN_EDIT_AGENT(tool.id) : undefined;
  }
  return !isManaged && canConfigure ? ROUTES.ADMIN_AGENT_CONFIG(tool.id) : undefined;
}

export function ToolsTableV2Row({
  tool,
  showType,
  showLastModified,
  isManaged,
  isExternalToolsAgent,
  onManageWorkspaces,
  onEditAgent,
  onDeleteAgent,
}: ToolsTableV2RowProps) {
  const navigate = useNavigate();

  const status = tool.configurationStatus;
  const unconfiguredCount = status?.unconfiguredTools.length ?? 0;
  // The tree is the authority once it is there; the outstanding count is the only signal left
  // when it is not.
  const hasConfigurable = status?.agentTree
    ? collectAllTools(status.agentTree).some((child) => child.isConfigurable)
    : unconfiguredCount > 0;
  // Every system agent has a config page (at least its Basic Information block), and the
  // external-tools agent has its own.
  const canConfigure = hasConfigurable || tool.source !== 'user' || isExternalToolsAgent;
  // Only the outstanding state earns a badge; a configured tool, or one partly set up in some
  // workspaces, reads as nothing to act on from this list. The badge is a count, so a tree that
  // reads as unconfigured with nothing listed outstanding raises none rather than "0 tools".
  const isUnconfigured =
    unconfiguredCount > 0 &&
    hasConfigurable &&
    !(status?.allHaveOrgConfig ?? false) &&
    tool.fullyConfiguredWorkspaces.length === 0;
  const detailRoute = getDetailRoute({
    tool,
    canConfigure,
    isManaged,
    canEditAgent: onEditAgent !== undefined,
  });

  return (
    // A row with nowhere to go keeps the hover tint without the `interactive` pointer. The
    // tint overrides the variant's own, and the tween comes from the primitive.
    <DatatableRow
      interactive={detailRoute !== undefined}
      className="h-[72px] hover:[&>*]:bg-surface"
      {...(detailRoute ? { onClick: () => void navigate(detailRoute) } : {})}
    >
      <DatatableCell>
        <ToolNameCell
          tool={tool}
          {...(isUnconfigured ? { unconfiguredCount } : {})}
          {...(detailRoute ? { href: detailRoute } : {})}
        />
      </DatatableCell>

      {showType && (
        <DatatableCell>
          <ToolTypeCell
            isCustom={tool.source === 'user'}
            {...(tool.builtAgent?.ownerUserId ? { ownerUserId: tool.builtAgent.ownerUserId } : {})}
          />
        </DatatableCell>
      )}

      <DatatableCell>
        <WorkspaceAccessCell
          workspaceAccess={tool.workspaceAccess}
          workspaces={tool.workspaces}
          isPrivate={tool.builtAgent?.isPrivate ?? false}
        />
      </DatatableCell>

      {showLastModified && (
        <DatatableCell>
          <span className="text-body-md text-muted-foreground">
            {tool.updatedAt ? formatRelativeDate(tool.updatedAt) : EM_DASH}
          </span>
        </DatatableCell>
      )}

      {/* The row opens the tool on click, and React bubbles through the menu's portal, so no
          click in here may reach it. */}
      <DatatableCell
        className={TABLE_COL_WIDTH.actions}
        onClick={(event) => event.stopPropagation()}
      >
        <ToolRowActions
          tool={tool}
          canConfigure={canConfigure}
          isManaged={isManaged}
          onManageWorkspaces={onManageWorkspaces}
          onEditAgent={onEditAgent}
          onDeleteAgent={onDeleteAgent}
        />
      </DatatableCell>
    </DatatableRow>
  );
}
